import React, { useRef, forwardRef } from "react";
import styles from "./generic-list-item.module.css";
import Checkbox from "frontend/ui-components/design-system/checkbox/checkbox";
import { useIsTextOverflowing } from "frontend/hooks/use-is-text-overflowing";
import { useHover } from "frontend/ui-components/floaters/use-hover";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import cn from "classnames";
import { useSearchHighlight } from "../../hooks/use-search-highlight";

export interface GenericListItemProps {
  id: string;
  title: string;
  indicatorColor?: string;
  isSelected: boolean;
  onSelect: (id: string, checked: boolean) => void;
  onClick?: () => void;
  rightContent?: React.ReactNode;
  searchValue?: string;
  showCheckbox?: boolean;
  showIndicator?: boolean;
  className?: string;
  titleClassName?: string;
}

export const GenericListItem = forwardRef<HTMLDivElement, GenericListItemProps>(
  (
    {
      id,
      title,
      indicatorColor,
      isSelected,
      onSelect,
      onClick,
      rightContent,
      searchValue,
      showCheckbox = true,
      showIndicator = true,
      className,
      titleClassName,
    },
    ref
  ) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const isOverflowing = useIsTextOverflowing(textRef);
    const textHover = useHover(textRef);
    const highlightStyles = useSearchHighlight(title, searchValue, textRef);

    const handleClick = () => {
      if (onClick) {
        onClick();
      } else if (showCheckbox) {
        onSelect(id, !isSelected);
      }
    };

    const handleCheckboxChange = (checked: boolean) => {
      onSelect(id, checked);
    };

    return (
      <div className={cn(styles.item, className)} onClick={handleClick} ref={ref}>
        {showIndicator && <div className={styles.indicator} style={{ backgroundColor: indicatorColor }} />}

        {showCheckbox && (
          <div className={styles.checkboxContainer}>
            <Checkbox checked={isSelected} onChange={handleCheckboxChange} />
          </div>
        )}

        <div className={styles.line} />

        <div className={cn(styles.itemTitle, titleClassName)}>
          {highlightStyles.map((style, index) => (
            <span key={index} className={styles.searchOverlay} style={style} />
          ))}
          <span ref={textRef} className={cn(styles.itemText, !rightContent && styles.itemTextWithoutRightContent)}>
            {title}
          </span>

          {textHover && isOverflowing && (
            <Tooltip
              key="title-tooltip"
              design="monday"
              label={title}
              relativeTo={textRef}
              side="bottom"
              customStyle={{ borderRadius: 3 }}
            />
          )}
        </div>

        {rightContent}
      </div>
    );
  }
);

GenericListItem.displayName = "GenericListItem";
